// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form-integrated event search component using react-hook-form.
 * This module provides a controlled search input for asset events that
 * integrates with form validation and state management through react-hook-form.
 * The component handles form state synchronization, validation display,
 * and accessibility requirements.
 *
 * Key features:
 * - Form state integration
 * - Validation handling
 * - Accessibility support
 * - Type-safe props
 * - Controlled inputs
 * - Error messaging
 * - Null safety
 */
import { EventSearch } from '@/asset/components/event-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Props for configuring the FormEventSearch component.
 * Extends react-hook-form's controller props for form integration.
 * Combines standard form field props with event search specific options.
 *
 * @typedef {Object} Props
 * @template Values - Type of the form values
 *
 * @example
 * ```ts
 * interface FormData {
 *   eventType: string;
 *   description: string;
 * }
 *
 * const props: Props<FormData> = {
 *   name: 'eventType',
 *   label: 'Event Type',
 *   control: useForm<FormData>().control
 * };
 * ```
 */
interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * Label text displayed above the input.
   * Required for accessibility and user guidance.
   * Should be descriptive and indicate the type of events
   * that can be searched.
   *
   * @example
   * "Search event types"
   * "Select event category"
   */
  label: string;

  /**
   * React Hook Form control object.
   * Used to register the field and manage its state.
   * Must be from the same form instance that defines
   * the field specified in 'name'.
   */
  control: Control<Values>;
}

/**
 * A form-integrated event search component with validation support.
 * Wraps the EventSearch component with react-hook-form integration,
 * providing form state management and validation capabilities.
 *
 * Features:
 * - Form state integration through react-hook-form
 * - Validation error display with custom messages
 * - Controlled value management with null safety
 * - Automatic error state handling
 * - Accessible label and error association
 * - Type-safe form integration
 * - Ref forwarding for focus management
 *
 * Form Integration:
 * - Automatically registers with react-hook-form
 * - Handles field validation state
 * - Manages error messages
 * - Syncs with form values
 *
 * Validation:
 * - Displays error states from form validation
 * - Shows error messages below input
 * - Handles required field validation
 * - Supports custom validation rules
 *
 * Usage example:
 * ```tsx
 * interface FormData {
 *   eventType: string;
 *   description: string;
 * }
 *
 * function EventForm() {
 *   const { control } = useForm<FormData>({
 *     defaultValues: {
 *       eventType: '',
 *       description: ''
 *     }
 *   });
 *
 *   return (
 *     <FormEventSearch
 *       name="eventType"
 *       label="Event type"
 *       control={control}
 *       rules={{ required: 'Event type is required' }}
 *     />
 *   );
 * }
 * ```
 *
 * @template Values - Type of the form values
 * @param props - Component configuration and form integration options
 */
export function FormEventSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined value must be
  // coalesced to null to maintain consistent behavior and prevent
  // uncontrolled to controlled warnings.
  const value = field.value ?? null;

  return (
    <EventSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      onChange={field.onChange}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
    />
  );
}
